import type {
  IComponent,
  LifeEventRestriction,
  LifeEventRestrictionComponent,
} from '@ben/shared/types/columbia';
import type { RestrictionSet } from '@ben/shared/types/restriction-set';
import { useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { StepsFields } from '../../elements/types';
import { isValidId } from './life-event-restriction-form.utils';

const useLifeEventRestrictions = (stepsControl: Control<StepsFields>) => {
  const existingSteps = useWatch({
    control: stepsControl,
    name: 'additionalConfig.steps' as const,
    defaultValue: [],
  });

  const existingServerRestrictions = useMemo(() => {
    return existingSteps.reduce<LifeEventRestriction[]>((accumulator, step) => {
      if (!isValidId(step.id) || !step.components) {
        return accumulator;
      }

      const allComponents = step.components.flatMap((component) =>
        isValidId(component.id)
          ? [toLifeEventRestrictionComponent(component)]
          : []
      );

      if (allComponents.length > 0) {
        accumulator.push({
          stepId: step.id,
          components: allComponents,
        });
      }

      return accumulator;
    }, []);
  }, [existingSteps]);

  return existingServerRestrictions;
};

const filterEmptyRestrictions = (
  restrictionSet: RestrictionSet
): RestrictionSet => {
  const filteredRestrictions: Record<string, unknown> = {};

  for (const key in restrictionSet) {
    const value = restrictionSet[key as keyof RestrictionSet];
    if (Array.isArray(value)) {
      if (value.length > 0) {
        filteredRestrictions[key] = value;
      }
    } else {
      filteredRestrictions[key] = value;
    }
  }

  return filteredRestrictions;
};

const getSubComponents = (
  component: IComponent
): LifeEventRestrictionComponent[] | undefined => {
  if (component.type !== 'MULTI_SELECT_WITH_SUBFORM' || !component.options) {
    return undefined;
  }

  const subComponents = component.options.flatMap((option) => {
    if (!option.components) {
      return [];
    }

    return option.components.flatMap((subComponent: IComponent) =>
      isValidId(subComponent.id)
        ? [toLifeEventRestrictionComponent(subComponent, false)]
        : []
    );
  });

  return subComponents.length > 0 ? subComponents : undefined;
};

const toLifeEventRestrictionComponent = (
  component: IComponent,
  includeSubComponents = true
): LifeEventRestrictionComponent => {
  const hasRestrictions =
    'restrictionSet' in component && component.restrictionSet != null;

  return {
    componentId: component.id as number,
    type: component.type,
    name: component.name,
    restrictionApplied: hasRestrictions,
    restrictionSet: hasRestrictions
      ? filterEmptyRestrictions(component.restrictionSet as RestrictionSet)
      : undefined,
    subComponents: includeSubComponents
      ? getSubComponents(component)
      : undefined,
  };
};

export default useLifeEventRestrictions;
